import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

export interface DialCreditBalance {
  balanceCents: number;
  balanceDollars: number;
  estimatedMinutesRemaining: number;
  totalPurchasedCents: number;
  totalUsedCents: number;
  totalMinutesUsed: number;
  lowBalanceThresholdCents: number;
  isLowBalance: boolean;
  costPerMinuteCents: number;
  /** Direct 130 agents only (else null): their dialing runs on this wallet until they're over the bar. */
  direct130Dialing?: Direct130Dialing | null;
}

export interface Direct130Dialing {
  /** Dialing draws on the wallet right now (Quote & Enroll issued premium, last 30 days, under the threshold). */
  metered: boolean;
  issuedPremium30d: number;
  threshold: number;
  /**
   * Telnyx's own two parts, in tenths of a cent (one decimal): per started minute out, per started minute in, and per
   * answered call out (its answering-machine detection). The true-up settles each call to the exact bill.
   */
  millsPerMinute: number;
  inboundMillsPerMinute: number;
  callFeeMills: number;
  /** Below zero when a call ran past it (Direct 130); more than $10 owed closes the CRM until paid. */
  balanceCents: number;
}

/** A 1-minute call out: the dearest minute, since the per-call fee lands on it. */
const oneMinuteCallMills = (d: Direct130Dialing): number => d.millsPerMinute + (d.callFeeMills ?? 0);

/** Minutes a balance buys at least: as if every call lasted a minute. Longer calls stretch it. */
export const direct130Minutes = (cents: number, d: Direct130Dialing): number => {
  const mills = oneMinuteCallMills(d);
  return mills > 0 ? Math.max(0, Math.floor((cents * 10) / mills)) : 0;
};

/** The backend's dial gate (utils/direct130-dialing assertDirect130CanDial): a free agent, or a 1-minute call's charge left. */
export const direct130CanDial = (d: Direct130Dialing): boolean =>
  !d.metered || d.balanceCents >= Math.max(1, Math.ceil(oneMinuteCallMills(d) / 10));

/** What a call out costs, in the carrier's two parts: "$0.0130/min + $0.0065/call". */
export const direct130RateLabel = (d: Direct130Dialing): string =>
  `$${(d.millsPerMinute / 1000).toFixed(4)}/min${d.callFeeMills ? ` + $${(d.callFeeMills / 1000).toFixed(4)}/call` : ""}`;

export interface DialCreditConfig {
  costPerMinuteCents: number;
  minPurchaseCents: number;
  maxPurchaseCents: number;
  suggestedAmounts: number[];
}

export type DialCreditTxnType =
  | "purchase"
  | "usage"
  | "refund"
  | "grant"
  | "promo"
  | "adjustment";

export interface DialCreditTransaction {
  id: string;
  type: DialCreditTxnType;
  amountCents: number;
  balanceAfterCents: number;
  description?: string;
  durationSeconds?: number;
  costPerMinuteCents?: number;
  stripeSessionId?: string;
  aiCallAttemptId?: string;
  createdAt: string;
}

export interface DialCreditCheckoutResult {
  url: string;
  sessionId: string;
  amountCents: number;
}

function unwrap<T>(result: unknown, fallback?: T): T {
  if (result && typeof result === "object" && "data" in result) {
    return (result as Record<string, unknown>).data as T;
  }
  return (result as T) ?? (fallback as T);
}

export const getDialCreditBalance = async (): Promise<DialCreditBalance> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(endpoint.dialCredits.balance), auth: true }),
  );
  return unwrap<DialCreditBalance>(result);
};

export const getDialCreditConfig = async (): Promise<DialCreditConfig> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(endpoint.dialCredits.config), auth: true }),
  );
  return unwrap<DialCreditConfig>(result);
};

export const getDialCreditTransactions = async (
  params: { type?: DialCreditTxnType; limit?: number; offset?: number } = {},
): Promise<{ items: DialCreditTransaction[]; total: number }> => {
  const qs = new URLSearchParams();
  if (params.type) qs.set("type", params.type);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.offset) qs.set("offset", String(params.offset));
  const qsStr = qs.toString();
  const url = qsStr
    ? `${endpoint.dialCredits.transactions}?${qsStr}`
    : endpoint.dialCredits.transactions;

  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(url), auth: true }),
  );
  return unwrap<{ items: DialCreditTransaction[]; total: number }>(result, {
    items: [],
    total: 0,
  });
};

export const createDialCreditCheckout = async (
  amountCents: number,
): Promise<DialCreditCheckoutResult> => {
  const result = await handleApiCall(async () =>
    api.post({
      url: getApiUrl(endpoint.dialCredits.checkout),
      body: { amountCents },
      auth: true,
    }),
  );
  return unwrap<DialCreditCheckoutResult>(result);
};

export const updateDialCreditSettings = async (settings: {
  lowBalanceThresholdCents?: number;
  autoTopupEnabled?: boolean;
  autoTopupAmountCents?: number;
}): Promise<void> => {
  await handleApiCall(async () =>
    api.patch({
      url: getApiUrl(endpoint.dialCredits.settings),
      body: settings,
      auth: true,
    }),
  );
};
